import { Head, useForm, usePage } from "@inertiajs/react"
import { Eye, EyeOff, LoaderCircle, Lock, Mail } from "lucide-react"
import { useState } from "react"
import type { FormEvent } from "react"
import { Checkbox, Button, Input, Label } from "../../components/ui"
import type { SharedPageProps } from "../../types/shared"

type LoginProps = {
  errors?: {
    email?: string[]
    password?: string[]
  }
}

export default function Login({ errors }: LoginProps) {
  const { props } = usePage<SharedPageProps>()
  const form = useForm({
    user: {
      email: "",
      password: "",
      remember_me: false
    }
  })

  const [showPassword, setShowPassword] = useState(false)

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.post("/users/sign_in")
  }

  return (
    <>
      <Head title="Entrar" />

      <main className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-white px-4 py-12 text-zinc-900">
        <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-indigo-100 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-0 h-72 w-72 rounded-full bg-violet-100 blur-3xl" />
        <div className="w-full max-w-md">
          {props.flash.alert && (
            <p className="mb-4 rounded-xl border border-rose-300 bg-rose-100 px-3 py-2 text-sm text-rose-700">
              {props.flash.alert}
            </p>
          )}

          <div className="mb-8 flex flex-col items-center">
            <div className="mb-6 flex w-full max-w-xs items-center justify-center rounded-2xl bg-white px-6 py-4 shadow-lg shadow-indigo-100 ring-1 ring-zinc-100">
              <img
                src="/TUEV-Rheinland-Logo1.svg.png"
                alt="TÜV Rheinland"
                className="h-12 w-auto max-w-full object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Portal TUV</h1>
            <p className="mt-2 text-center text-sm text-zinc-500">
              Entre na sua conta para continuar
            </p>
          </div>

          <form onSubmit={submit} className="flex flex-col gap-5 rounded-3xl bg-white/90 p-6 shadow-xl shadow-zinc-200/60 ring-1 ring-zinc-200 backdrop-blur">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-zinc-900">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="border-zinc-300 bg-white pl-10 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-zinc-700"
                  autoComplete="email"
                  required
                  value={form.data.user.email}
                  onChange={(event) =>
                    form.setData("user", { ...form.data.user, email: event.target.value })
                  }
                />
              </div>
              {errors?.email?.[0] && <p className="text-xs text-rose-600">{errors.email[0]}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password" className="text-zinc-900">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="border-zinc-300 bg-white pl-10 pr-10 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-zinc-700"
                  autoComplete="current-password"
                  required
                  value={form.data.user.password}
                  onChange={(event) =>
                    form.setData("user", { ...form.data.user, password: event.target.value })
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors hover:text-zinc-700"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors?.password?.[0] && <p className="text-xs text-rose-600">{errors.password[0]}</p>}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                className="border-zinc-300 bg-white text-zinc-900 checked:border-zinc-900 checked:bg-zinc-900"
                checked={Boolean(form.data.user.remember_me)}
                onChange={(e) =>
                  form.setData("user", { ...form.data.user, remember_me: e.target.checked })
                }
              />
              <Label htmlFor="remember" className="cursor-pointer text-sm font-normal text-zinc-900">
                Lembrar de mim
              </Label>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-500 hover:to-violet-500"
              disabled={form.processing}
            >
              {form.processing ? (
                <span className="flex items-center gap-2">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  Entrando...
                </span>
              ) : (
                "Entrar"
              )}
            </Button>
          </form>

          <footer className="mt-10 text-center text-sm text-zinc-500">
            © {new Date().getFullYear()} Portal TUV. Todos os direitos reservados.
          </footer>
        </div>
      </main>
    </>
  )
}
